// 备货分析 (Phase 3 · T9/T10) — the plan-vs-reality report, shown as a mode
// inside 统计报表. Per product over a window:
//   订单 = certified order pickups · 现货 = certified actual walk-in sold ·
//   总数 = 订单 + 现货 (toggle to exclude 订单) · 计划 = stock plan ·
//   准确率 = plan vs actual walk-in (certified days only).
// Uncertified days fall back to live actuals with an amber 未认证 tag.
// (Prediction/trend features deferred — sparse data = noise, not signal.)

import { useEffect, useMemo, useState } from 'react';
import * as api from '../lib/api.ts';
import { displayName } from '../lib/products.ts';
import { torontoToday, torontoNDaysAgo } from '../lib/torontoTime.js';
import { Icon } from './Icon.jsx';
import { Toast } from './atoms.jsx';

const PRESETS = [
  { key: '7d', label: '最近 7 天', from: () => torontoNDaysAgo(6) },
  { key: '30d', label: '最近 30 天', from: () => torontoNDaysAgo(29) },
  { key: '90d', label: '最近 90 天', from: () => torontoNDaysAgo(89) }
];
const STORE_OPTS = [{ k: 'all', l: '全部门店' }, { k: 'NY', l: 'NY' }, { k: 'DT', l: 'DT' }, { k: 'RH', l: 'RH' }];

function pctText(a) { return a == null ? '—' : `${Math.round(a * 100)}%`; }
function accClass(a) { return a == null ? '' : a >= 0.85 ? 'pa-good' : a >= 0.6 ? 'pa-warn' : 'pa-bad'; }

export function PrepAnalysis() {
  const [preset, setPreset] = useState('30d');
  const [from, setFrom] = useState(() => torontoNDaysAgo(29));
  const [to, setTo] = useState(() => torontoToday());
  const [store, setStore] = useState('all');
  const [includeOrders, setIncludeOrders] = useState(true);
  const [sort, setSort] = useState({ key: 'total', dir: 'desc' });
  const [rows, setRows] = useState(null);
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState(null);

  function load(f = from, t = to, s = store) {
    setLoading(true);
    const st = s === 'all' ? undefined : s;
    api.loadPrepReport(f, t, st)
      .then(rep => setRows(rep.rows || []))
      .catch(e => setToast({ kind: 'error', msg: e?.message || '加载失败' }))
      .finally(() => setLoading(false));
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  function applyPreset(key) {
    const p = PRESETS.find(x => x.key === key); if (!p) return;
    const f = p.from(); const t = torontoToday();
    setPreset(key); setFrom(f); setTo(t); load(f, t, store);
  }
  function changeStore(s) { setStore(s); load(from, to, s); }
  function changeSort(key) { setSort(s => s.key === key ? { key, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'desc' }); }

  const sorted = useMemo(() => {
    if (!rows) return [];
    const total = r => includeOrders ? r.total : r.stock;
    const val = r => {
      switch (sort.key) {
        case 'orders': return r.orders;
        case 'stock': return r.stock;
        case 'total': return total(r);
        case 'planned': return r.planned;
        case 'accuracy': return r.accuracy == null ? -1 : r.accuracy;
        default: return total(r);
      }
    };
    const dir = sort.dir === 'asc' ? 1 : -1;
    return [...rows].sort((a, b) => (val(a) - val(b)) * dir || (b.total - a.total));
  }, [rows, sort, includeOrders]);

  const Th = ({ k, label, num }) => (
    <th className={`${num ? 'num' : ''} sortable`} onClick={() => changeSort(k)}>
      {label} {sort.key === k && <span className="sort-ind">{sort.dir === 'asc' ? '↑' : '↓'}</span>}
    </th>
  );

  return (
    <>
      <div className="inv-toolbar">
        <div className="inv-segmented" role="tablist" aria-label="门店筛选">
          {STORE_OPTS.map(o => (
            <button key={o.k} role="tab" aria-selected={store === o.k} disabled={loading && store !== o.k} onClick={() => changeStore(o.k)}>{o.l}</button>
          ))}
        </div>
        <label className="inv-checkbox-label">
          <input type="checkbox" checked={includeOrders} onChange={e => setIncludeOrders(e.target.checked)} />
          总数含订单
        </label>
        <span className="vava-hint" style={{ marginLeft: 8 }}>现货 = 已认证实际散卖 · 准确率 = 计划 vs 实际散卖</span>
      </div>

      <div className="inv-filter-card">
        {PRESETS.map(p => (
          <button key={p.key} className={preset === p.key ? 'btn btn-primary' : 'btn btn-secondary'} disabled={loading} onClick={() => applyPreset(p.key)}>{p.label}</button>
        ))}
        <span className="inv-divider" />
        <label className="vava-hint">从</label>
        <input type="date" className="vava-input inv-date-input" value={from} max={to} disabled={loading} onChange={e => setFrom(e.target.value)} />
        <label className="vava-hint">到</label>
        <input type="date" className="vava-input inv-date-input" value={to} max={torontoToday()} disabled={loading} onChange={e => setTo(e.target.value)} />
        <button className="btn btn-secondary" disabled={loading} onClick={() => { setPreset(''); load(from, to, store); }}>查询</button>
      </div>

      {!rows ? (
        <div className="vava-card empty-state">加载中…</div>
      ) : sorted.length === 0 ? (
        <div className="pa-wrap"><div className="inv-empty"><div className="emoji">📊</div><div className="title">所选区间没有备货 / 销售数据</div><div className="sub">试试更大的区间，或先在「备货录入」录入并在「当日现货」认证</div></div></div>
      ) : (
        <div className="pa-wrap">
          <table className="inv-grid pa-table">
            <thead>
              <tr>
                <th>产品</th>
                <Th k="orders" label="订单" num />
                <Th k="stock" label="现货" num />
                <Th k="total" label="总数" num />
                <Th k="planned" label="计划" num />
                <Th k="accuracy" label="准确率" num />
                <th className="num">已认证</th>
              </tr>
            </thead>
            <tbody>
              {sorted.map(r => (
                <tr key={r.productId}>
                  <td>{displayName(r)}{r.hasUncertified && <span className="pa-uncert" title="部分日期未认证，现货含实时估算"><Icon name="alert" /> 未认证</span>}</td>
                  <td className="num">{r.orders || ''}</td>
                  <td className="num">{r.stock || ''}</td>
                  <td className="num"><b>{includeOrders ? r.total : r.stock}</b></td>
                  <td className="num">{r.planned || ''}</td>
                  <td className={`num ${accClass(r.accuracy)}`}>{r.accuracy == null ? <span className="pa-cold">数据不足</span> : pctText(r.accuracy)}</td>
                  <td className="num pa-muted">{r.certifiedDays || ''}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {toast && <Toast message={toast.msg} kind={toast.kind} onDone={() => setToast(null)} />}
    </>
  );
}
